import React from "react";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import { Skeleton, Stack } from "@mui/material";
import {
  BackButton,
  CancelButton,
  DeleteButton,
  SaveButton,
} from "../../components/common/MainButtonComponent";
import MainButton from "../../components/common/MainButtonComponent";
import { useQuestionEditor } from "../../context/assessSections/QuestionEditorContext";
import "./AssessShared.css";
import "./QuestionLibrary.css";
import "../question/CreateAddAssessmentQuestion.css";
import PassageModal from "./PassageModal";
import PassageEditor from "../../components/common/editor/ViewEditor";
import FileUpload from "../../components/common/form/FileUpload";
import { assessmentService } from "../../service/assessment/assessmentService";
import CustomEditor from "../../components/common/editor/CustomEditor";
import GuidanceEditor from "../../components/common/editor/GuidanceEditor";
import ViewEditor from "../../components/common/editor/ViewEditor";

const selectPlaceholder = "Nothing selected";

const buildMetaFields = (meta) => [
  {
    name: "selectedType",
    label: "Type",
    options: meta.types,
    valueKey: "type_id",
    labelKey: "qtype",
  },
  {
    name: "selectedAnswerType",
    label: "Answer Type",
    options: meta.answer_types,
    valueKey: "answer_type_id",
    labelKey: "answer_type",
  },
  {
    name: "selectedCategory",
    label: "Category",
    options: meta.categories,
    valueKey: "category_id",
    labelKey: "name",
  },
  {
    name: "selectedDifficulty",
    label: "Difficulty",
    options: meta.difficulty_types,
    valueKey: "difficulty_id",
    labelKey: "difficulty_type",
  },
  {
    name: "selectedGrade",
    label: "Grade",
    options: meta.grades,
    valueKey: "grade_id",
    labelKey: "grade",
    labelPrefix: "Grade ",
  },
  {
    name: "selectedSubject",
    label: "Subject",
    options: meta.subjects,
    valueKey: "subject_id",
    labelKey: "subject",
  },
  {
    name: "selectedLanguage",
    label: "Language",
    options: meta.languages,
    valueKey: "language_id",
    labelKey: "language",
  },
  {
    name: "selectedTerm",
    label: "Term/Semester",
    options: meta.terms,
    valueKey: "term_id",
    labelKey: "term",
  },
  {
    name: "selectedChapter",
    label: "Chapter/Module/Unit",
    options: meta.chapters,
    valueKey: "chapter_id",
    labelKey: "chapter",
  },
  {
    name: "selectedYear",
    label: "Year",
    options: meta.years,
    valueKey: "year_id",
    labelKey: "year",
  },
  {
    name: "selectedSource",
    label: "Source",
    options: meta.sources,
    valueKey: "source_id",
    labelKey: "source",
  },
  {
    name: "selectedAuthor",
    label: "Author",
    options: meta.authors,
    valueKey: "author_id",
    labelKey: "author",
  },
  {
    name: "selectedPrivilege",
    label: "Privilege",
    options: meta.privileges,
    valueKey: "privilege_id",
    labelKey: "privilege",
  },
];

const isEditorEmpty = (editorState) => {
  const raw = convertToRaw(editorState.getCurrentContent());

  const hasText = raw.blocks.some((block) => block.text.trim() !== "");
  const hasEntity = Object.keys(raw.entityMap || {}).length > 0;

  return !hasText && !hasEntity;
};

const showAlert = ({ title, text, icon }) => {
  if (window.Swal) {
    window.Swal.fire({
      title,
      text,
      icon,
    });
  } else {
    alert(text);
  }
};


export const RichQuestionEditor = ({
  editorState,
  onEditorStateChange,
  readOnly = false,
  type = "content",
}) => {
  if (readOnly) {
    return (
      <div className="RichEditor-root-createquestion">
        <ViewEditor editorState={editorState} />
      </div>
    );
  }

  if (type === "guidance") {
    return (
      <div className="RichEditor-root-createquestion">
        <GuidanceEditor
          value={editorState}
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
        />
      </div>
    );
  }

  return (
    <div className="RichEditor-root-createquestion">
      <CustomEditor
        value={editorState}
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
      />
    </div>
  );
};

function normalizeAssessmentParts(parts = []) {
  return parts
    .map((part) => ({
      ...part,
      assessment_part_sections: Array.from(
        new Map(
          (part.assessment_part_sections || []).map((section) => [
            section.part_section_id,
            section,
          ])
        ).values()
      ),
    }))
    .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0));
}

function getPassageContent(content) {
  try {
    if (!content) return EditorState.createEmpty();

    const parsedContent = JSON.parse(content);
    const rawContent = Array.isArray(parsedContent)
      ? parsedContent[0]
      : parsedContent;

    return EditorState.createWithContent(convertFromRaw(rawContent));
  } catch {
    return EditorState.createEmpty();
  }
}

const MetaSelect = ({ field, value, onChange, disabled }) => (
  <label className="question-side-field">
    <span>{field.label}</span>

    <select
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(field.name, e.target.value)}
    >
      <option value="">{selectPlaceholder}</option>

      {field.options.map((option) => (
        <option key={option[field.valueKey]} value={option[field.valueKey]}>
          {field.labelPrefix || ""}
          {option[field.labelKey]}
        </option>
      ))}
    </select>
  </label>
);


const QuestionOption = ({
  index,
  editorState,
  marks,
  onRemove,
  onEditorChange,
  onMarksChange,
  readOnly,
}) => (
  <div className="question-option-block">
    <div className="question-option-block__head">
      <div>
        <h3>Choice {index + 1}</h3>

        <label className="question-correct-check">
          <input
            type="checkbox"
            checked={Number(marks) > 0}
            disabled={readOnly}
            onChange={() => onMarksChange(index)}
          />
          <span>Correct Answer</span>
        </label>
      </div>

      {!readOnly && (
        <DeleteButton label="Remove" onClick={() => onRemove(index)} />
      )}
    </div>

    <RichQuestionEditor
      readOnly={readOnly}
      editorState={editorState}
      onEditorStateChange={(nextEditorState) =>
        onEditorChange(index, nextEditorState)
      }
    />
  </div>
);

export default function QuestionEditorForm({
  title,
  mode = "create",
  // by default assesment is set to false
  withAssessment = false,
  onBack,
  onCancel,
  onSave,
  showCreateButton = true,
}) {
  const readOnly = mode === "view";
  const [passageModalMode, setPassageModalMode] = React.useState(null);

  const {
    form,
    meta,
    metaLoading,
    metaError,
    editLoading,
    editError,
    saving,
    updateField,
    addOption,
    removeOption,
    updateChoiceEditor,
    updateChoiceMarks,
    resetForm,
    saveMCQQuestion,
    saveDescriptiveQuestion,
    selectedPassageDetails,
    selectedPassageLoading,
    selectedPassageError,
    loadSelectedPassage,
    createPassage,
    editPassage,
    addSubQuestion,
    removeSubQuestion,
    updateSubQuestionEditor,
    updateSubQuestionAnswerEditor,
    saveSubQuestionBundle,
    getSubmissionContent,
    deleteSubmissionContent,
    saveSubmissionQuestion,
    createQuestionWithAssessment,
    updateQuestion,
  } = useQuestionEditor();

  //const metaFields = buildMetaFields(meta);
  const metaFields = React.useMemo(() => buildMetaFields(meta), [meta]);
  const selectedTypeName =
    meta.types.find((type) => String(type.type_id) === String(form.selectedType))
      ?.qtype || "";

  const showAnswerType = selectedTypeName === "MCQ";
  const showOptions = selectedTypeName === "MCQ";
  const showDescriptiveAnswer = selectedTypeName === "Descriptive";
  const showSubQuestions = selectedTypeName === "Sub Question";
  const showSubmissionAnswer = selectedTypeName === "Submission";

  const getShortCode = React.useCallback((list, id, idKey) => {
    const item = list.find((x) => String(x[idKey]) === String(id));
    return item?.short_code || null;
  }, []);

  const buildTitlePrefix = React.useCallback(
    (assessment) => {
      const gradeCode = getShortCode(meta.grades, assessment?.grade_id, "grade_id");
      const subjectCode = getShortCode(meta.subjects, assessment?.subject_id, "subject_id");
      const languageCode = getShortCode(meta.languages, assessment?.language_id, "language_id");
      const termCode = getShortCode(meta.terms, assessment?.term_id, "term_id");

      const parts = [gradeCode, subjectCode, languageCode, termCode].filter(Boolean);

      return parts.length ? `${parts.join("-")}-` : "";
    },
    [meta.grades, meta.subjects, meta.languages, meta.terms, getShortCode]
  );

  const handlePassageYes = () => {
    updateField("selectedIsPassage", true);
  };

  const handlePassageNo = () => {
    updateField("selectedIsPassage", false);
    updateField("selectedPassageType", "");
    updateField("selectedPassage", "");
    loadSelectedPassage(null);
  };

  const handleCancel = () => {
    if (window.Swal) {
      window.Swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, cancel it!",
          cancelButtonText: "No",
        })
        .then((result) => {
          if (result.isConfirmed) {
            onCancel && onCancel();
          }
        });

      return;
    }

    const confirmed = window.confirm("Are you sure you want to cancel?");
    if (confirmed) {
      onCancel && onCancel();
    }
  };

  const handleCreateNewQuestion = React.useCallback(() => {
    resetForm();

    if (withAssessment) {
      createQuestionWithAssessment(null);
    }
  }, [resetForm, withAssessment, createQuestionWithAssessment]);

  const keepPreviousCriteria = React.useCallback(() => {
    const previousCriteria = {
      selectedAssessmentId: form.selectedAssessmentId,
      selectedPart: form.selectedPart,
      selectedSection: form.selectedSection,
      assessmentParts: form.assessmentParts,
      assessmentSections: form.assessmentSections,

      selectedType: form.selectedType,
      selectedAnswerType: form.selectedAnswerType,
      selectedCategory: form.selectedCategory,
      selectedDifficulty: form.selectedDifficulty,
      selectedGrade: form.selectedGrade,
      selectedSubject: form.selectedSubject,
      selectedLanguage: form.selectedLanguage,
      selectedTerm: form.selectedTerm,
      selectedChapter: form.selectedChapter,
      selectedYear: form.selectedYear,
      selectedSource: form.selectedSource,
      selectedAuthor: form.selectedAuthor,
      selectedPrivilege: form.selectedPrivilege,
      selectedTag: form.selectedTag,
      selectedIsPassage: form.selectedIsPassage,
      selectedPassageType: form.selectedPassageType,
      selectedPassage: form.selectedPassage,
    };

    const selectedAssessment = meta.assessments.find(
      (item) =>
        String(item.assessment_id) === String(previousCriteria.selectedAssessmentId)
    );

    resetForm();

    if (withAssessment) {
      createQuestionWithAssessment(selectedAssessment || null);
    }

    Object.entries(previousCriteria).forEach(([key, value]) => {
      updateField(key, value);
    });

    updateField(
      "selectedTitle",
      selectedAssessment ? buildTitlePrefix(selectedAssessment) : ""
    );

    if (previousCriteria.selectedPassage) {
      loadSelectedPassage(previousCriteria.selectedPassage);
    }
  }, [
    form,
    meta.assessments,
    resetForm,
    updateField,
    withAssessment,
    createQuestionWithAssessment,
    buildTitlePrefix,
    loadSelectedPassage,
  ]);

  const showSuccessFlow = ({ text, result }) => {
    if (window.Swal) {
      window.Swal
        .fire({
          title: "Success!",
          text,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Create Another Question",
          cancelButtonText: "Back to Questions Library",
          allowOutsideClick: false,
        })
        .then((swalResult) => {
          if (swalResult.isConfirmed) {
            keepPreviousCriteria();
            return;
          }

          onSave && onSave(result);
        });

      return;
    }

    onSave && onSave(result);
  };

  const handleAssessmentChange = async (assessmentId) => {
    const assessment = meta.assessments.find(
      (item) => String(item.assessment_id) === String(assessmentId)
    );

    createQuestionWithAssessment(assessment || null);

    updateField(
      "selectedTitle",
      assessment ? buildTitlePrefix(assessment) : ""
    );

    if (!assessment?.assessment_id) return;

    try {
      const response = await assessmentService.getSectionsAndPartsByAssessment(
        assessment.assessment_id
      );

      if (response?.success) {
        // updateField("assessmentParts", response.data?.parts || []);
        updateField("assessmentParts", normalizeAssessmentParts(response.data?.parts || []));
        updateField("assessmentSections", []);
        updateField("selectedPart", null);
        updateField("selectedSection", null);
      }
    } catch (error) {
      showAlert({
        title: "Failed!",
        text: error?.message || "Failed to load assessment parts",
        icon: "error",
      });
    }
  };

  const handlePartChange = (partId) => {
    const selectedPart = form.assessmentParts.find(
      (part) => String(part.assessment_part_id) === String(partId)
    );

    updateField("selectedPart", partId);
    updateField("selectedSection", null);
    updateField(
      "assessmentSections",
      selectedPart?.assessment_part_sections || []
    );
  };

  const handleSave = async () => {
    if (withAssessment && !form.selectedAssessmentId) {
      showAlert({
        title: "Failed!",
        text: "Assessment title is required",
        icon: "error",
      });
      return;
    }

    if (
      withAssessment &&
      form.assessmentParts.length > 0 &&
      (!form.selectedPart || !form.selectedSection)
    ) {
      showAlert({
        title: "Failed!",
        text: "Please select assessment part and section",
        icon: "error",
      });
      return;
    }
    if (!form.selectedTitle.trim()) {
      showAlert({
        title: "Failed!",
        text: "Question title is required",
        icon: "error",
      });
      return;
    }

    if (isEditorEmpty(form.contentEditorState)) {
      showAlert({
        title: "Failed!",
        text: "Question content is required",
        icon: "error",
      });
      return;
    }

    if (form.selectedIsPassage && !form.selectedPassage) {
      showAlert({
        title: "Failed!",
        text: "Please select a passage",
        icon: "error",
      });
      return;
    }

    if (selectedTypeName === "MCQ") {
      if (form.choicesEditorState.length === 0) {
        showAlert({
          title: "Failed!",
          text: "Please add at least one option",
          icon: "error",
        });
        return;
      }

      const hasEmptyChoice = form.choicesEditorState.some(isEditorEmpty);

      if (hasEmptyChoice) {
        showAlert({
          title: "Failed!",
          text: "Please complete all options",
          icon: "error",
        });
        return;
      }

      const selectedOptionCount = form.selectedChoices.filter(
        (mark) => Number(mark || 0) > 0
      ).length;

      if (selectedOptionCount === 0) {
        showAlert({
          title: "Failed!",
          text: "Please select at least one correct answer",
          icon: "error",
        });
        return;
      }
    }

    if (selectedTypeName === "Descriptive") {
      if (isEditorEmpty(form.descriptiveAnswerEditorState)) {
        showAlert({
          title: "Failed!",
          text: "Model answer is required",
          icon: "error",
        });
        return;
      }
    }

    if (selectedTypeName === "Sub Question") {
      const hasEmptySubQuestion = form.subQuestionEditorStates.some(isEditorEmpty);
      const hasEmptySubAnswer = form.subQuestionAnswerEditorStates.some(isEditorEmpty);

      if (hasEmptySubQuestion || hasEmptySubAnswer) {
        showAlert({
          title: "Failed!",
          text: "Please complete all sub questions and model answers",
          icon: "error",
        });
        return;
      }
    }

    if (selectedTypeName === "Submission") {
      if (!form.submissionFiles.length && !form.submissionAnswerContent.length) {
        showAlert({
          title: "Failed!",
          text: "Model answer file is required",
          icon: "error",
        });
        return;
      }

      if (!form.submissionUploaded) {
        showAlert({
          title: "Please wait!",
          text: "File upload is still in progress",
          icon: "info",
        });
        return;
      }
    }

    try {
      if (mode === "edit") {
        const result = await updateQuestion();

        showAlert({
          title: "Success!",
          text: "Question updated successfully",
          icon: "success",
        });

        onSave && onSave(result);
        onCancel && onCancel();

        return;
      }

      if (selectedTypeName === "Submission") {
        const result = await saveSubmissionQuestion();

        showSuccessFlow({
          text: "Added new Submission question",
        });

        // console.log("Saved Submission result:", result);
        return;
      }

      if (selectedTypeName === "MCQ") {
        const result = await saveMCQQuestion();

        showSuccessFlow({
          text: "Added new MCQ question",
          result,
        });

        //onSave && onSave(result);

        // console.log("Saved MCQ result:", result);
        return;
      }

      if (selectedTypeName === "Descriptive") {
        const result = await saveDescriptiveQuestion();

        showSuccessFlow({
          text: "Added new Descriptive question",
        });
        //onSave && onSave(result);

        // console.log("Saved Descriptive result:", result);
        return;
      }

      if (selectedTypeName === "Sub Question") {
        const result = await saveSubQuestionBundle();

        showSuccessFlow({
          text: "Added new Sub Question",
        });
        //onSave && onSave(result);
        // console.log("Saved Sub Question result:", result);
        return;
      }

      showAlert({
        title: "Not ready yet",
        text: "Only MCQ, Descriptive, submission and Sub Question save are connected for now.",
        icon: "info",
      });
    } catch (error) {
      showAlert({
        title: "Failed!",
        text: error?.message || "Failed to create question",
        icon: "error",
      });
    }
  };


  return (
    <section className="question-create-page">
      <div className="question-create-topbar">
        <BackButton label="Back" onClick={onBack} />

        <h1>{title}</h1>

        <div className="question-create-topbar__actions">
          {!readOnly && (
            <>
              {showCreateButton && (
                <MainButton
                  action="add"
                  icon="plus-circle"
                  label="Clear & Create New"
                  tone="primary"
                  onClick={handleCreateNewQuestion}
                />
              )}

              <SaveButton
                label={saving ? "Saving..." : mode === "edit" ? "Save Changes" : "Save"}
                onClick={handleSave}
                // disabled={saving || !form.submissionUploaded}
                disabled={
                  saving ||
                  (selectedTypeName === "Submission" && !form.submissionUploaded)
                }
              />

              <CancelButton label="Cancel" onClick={handleCancel} />
            </>
          )}
        </div>
      </div>

      <div className="question-create-layout row g-3">
        {metaLoading && (
          <div className="col-12">
            <div style={{ padding: 24 }}>
              <Stack spacing={2}>
                <Skeleton variant="rectangular" width="100%" height={48} />
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} variant="rectangular" width="100%" height={52} />
                ))}
              </Stack>
            </div>
          </div>
        )}

        {metaError && (
          <div className="col-12">
            <p className="text-danger">{metaError}</p>
          </div>
        )}

        {editLoading && (
          <div className="col-12">
            <div style={{ padding: 24 }}>
              <Stack spacing={2}>
                <Skeleton variant="rectangular" width="100%" height={48} />
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} variant="rectangular" width="100%" height={52} />
                ))}
              </Stack>
            </div>
          </div>
        )}

        {editError && (
          <div className="col-12">
            <p className="text-danger">{editError}</p>
          </div>
        )}

        <aside className="col-12 col-lg-3">
          <div className="question-side-panel">
            {withAssessment && (
              <>
                <label className="question-side-field">
                  <span>Assessment Title</span>
                  <select
                    disabled={readOnly}
                    value={form.selectedAssessmentId || ""}
                    onChange={(e) => handleAssessmentChange(e.target.value)}
                  >
                    <option value="">Nothing selected</option>

                    {meta.assessments.map((assessment) => (
                      <option
                        key={assessment.assessment_id}
                        value={assessment.assessment_id}
                      >
                        {assessment.title}
                      </option>
                    ))}
                  </select>
                </label>

                {form.selectedAssessmentId && (
                  <label className="question-side-field">
                    <span>Assessment Part</span>
                    <select
                      disabled={readOnly}
                      value={form.selectedPart || ""}
                      onChange={(e) => handlePartChange(e.target.value)}
                    >
                      <option value="">Nothing selected</option>

                      {form.assessmentParts.map((part) => (
                        <option
                          key={part.assessment_part_id}
                          value={part.assessment_part_id}
                        >
                          {part.part_title || part.part_name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {form.selectedAssessmentId && form.selectedPart && (
                  <label className="question-side-field">
                    <span>Assessment Section</span>
                    <select
                      disabled={readOnly}
                      value={form.selectedSection || ""}
                      onChange={(e) => updateField("selectedSection", e.target.value)}
                    >
                      <option value="">Nothing selected</option>

                      {form.assessmentSections.map((section) => (
                        <option
                          key={section.part_section_id}
                          value={section.part_section_id}
                        >
                          {section.section_title || section.section_name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            )}

            {metaFields
              // .filter((field) => withAssessment || field.label !== "Assessment Title")
              .filter((field) => showAnswerType || field.label !== "Answer Type")
              .map((field) => (
                <MetaSelect
                  key={field.name}
                  field={field}
                  value={form[field.name]}
                  onChange={updateField}
                  disabled={
                    readOnly ||
                    (withAssessment &&
                      !["selectedType", "selectedAnswerType", "selectedPrivilege"].includes(
                        field.name
                      ))
                  }
                />
              ))}

            <label className="question-side-field">
              <span>Tags</span>
              <input
                disabled={readOnly}
                value={form.selectedTag}
                onChange={(e) => updateField("selectedTag", e.target.value)}
              />
            </label>
          </div>
        </aside>
        
        <div className="col-12 col-lg-9">
          <div className="question-main-form">
            <label className="question-title-field">
              <span>Question Title</span>
              <input
                disabled={readOnly}
                value={form.selectedTitle}
                onChange={(e) => updateField("selectedTitle", e.target.value)}
              />
            </label>

            <div className="question-editor-section">
              <h2>Marking Guidance</h2>
              <RichQuestionEditor
                type="guidance"
                readOnly={readOnly}
                editorState={form.guidanceEditorState}
                onEditorStateChange={(editorState) =>
                  updateField("guidanceEditorState", editorState)
                }
              />
            </div>
              <>
                <div className="question-passage-toggle">
                  <h2>Do you want to add a passage?</h2>

                  <label>
                    Yes{" "}
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={form.selectedIsPassage}
                      onChange={handlePassageYes}
                    />
                  </label>

                  <label>
                    No{" "}
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={!form.selectedIsPassage}
                      onChange={handlePassageNo}
                    />
                  </label>
                </div>

                {form.selectedIsPassage && (
                  <div className="question-editor-section">
                    <h2>Passage Type</h2>

                    <select
                      className="question-passage-select"
                      value={form.selectedPassageType || ""}
                      disabled={readOnly}
                      onChange={(e) => {
                        updateField("selectedPassageType", e.target.value);
                        updateField("selectedPassage", "");
                        loadSelectedPassage(null);
                      }}
                    >
                      <option value="">Nothing selected</option>

                      {meta.passage_types.map((type) => (
                        <option key={type.passage_type_id} value={type.passage_type_id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {form.selectedIsPassage && form.selectedPassageType && (
                  <div className="question-editor-section">
                    <h2>Passage Title</h2>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        className="question-passage-select"
                        value={form.selectedPassage || ""}
                        disabled={readOnly}
                        onChange={(e) => {
                          updateField("selectedPassage", e.target.value);
                          loadSelectedPassage(e.target.value);
                        }}
                      >
                        <option value="">Nothing selected</option>

                        {meta.passages
                          .filter(
                            (passage) =>
                              String(passage.passage_type_id) === String(form.selectedPassageType)
                          )
                          .map((passage) => (
                            <option key={passage.passage_id} value={passage.passage_id}>
                              {passage.title}
                            </option>
                          ))}
                      </select>
                      {!readOnly && form.selectedPassage && (
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          title="Clear selected passage"
                          onClick={() => {
                            updateField("selectedPassage", "");
                            loadSelectedPassage(null);
                          }}
                        >
                          ×
                        </button>
                      )}


                      {!readOnly && (form.selectedPassage ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          onClick={() => setPassageModalMode("edit")}
                        >
                          Edit
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => setPassageModalMode("add")}
                        >
                          Add
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {form.selectedIsPassage && form.selectedPassage && (
                  <div className="question-editor-section">
                    <h2>Passage Content</h2>

                    {selectedPassageLoading && <p>Loading passage...</p>}

                    {selectedPassageError && (
                      <p className="text-danger">{selectedPassageError}</p>
                    )}

                    {selectedPassageDetails && (
                      <>
                        <PassageEditor
                          editorState={getPassageContent(selectedPassageDetails.content)}
                        />
                      </>
                    )}
                  </div>
                )}
              </>
            

            <div className="question-editor-section">
              <h2>{showSubQuestions ? "Main Question" : "Question Content"}</h2>

              <RichQuestionEditor
                readOnly={readOnly}
                editorState={form.contentEditorState}
                onEditorStateChange={(editorState) =>
                  updateField("contentEditorState", editorState)
                }
              />
            </div>

            {showOptions && (
              <>
                <div className="question-options-title">
                  <h2>Enter the options</h2>
                  <p>Select one or more correct answers.</p>
                </div>

                <div className="question-options-list">
                  {form.choicesEditorState.map((editorState, index) => (
                    <QuestionOption
                      key={index}
                      index={index}
                      editorState={editorState}
                      marks={form.selectedChoices[index]}
                      onRemove={removeOption}
                      onEditorChange={updateChoiceEditor}
                      onMarksChange={updateChoiceMarks}
                      readOnly={readOnly}
                    />
                  ))}
                </div>

                {!readOnly && (
                  <MainButton
                    action="add"
                    icon="add"
                    label="Add option"
                    tone="primary"
                    onClick={addOption}
                  />
                )}
              </>
            )}

            {showDescriptiveAnswer && (
              <div className="question-editor-section">
                <h2>Model Answer</h2>

                <RichQuestionEditor
                  readOnly={readOnly}
                  editorState={form.descriptiveAnswerEditorState}
                  onEditorStateChange={(editorState) =>
                    updateField("descriptiveAnswerEditorState", editorState)
                  }
                />
              </div>
            )}

            {showSubmissionAnswer && (
              <div className="question-editor-section">
                <h2>Model Answer</h2>

                <FileUpload
                  className="col-lg-12"
                  callBack={getSubmissionContent}
                  deleteback={deleteSubmissionContent}
                  enable={!readOnly}
                  clear={form.submissionClearFile}
                  defaultFile={form.submissionAnswerContent}
                />
              </div>
            )}

            {showSubQuestions && (
              <>
                <div className="question-options-title">
                  <h2>Sub Questions</h2>
                  <p>Add each sub question with its model answer.</p>
                </div>

                <div className="question-options-list">
                  {form.subQuestionEditorStates.map((editorState, index) => (
                    <div className="question-option-block" key={index}>
                      <div className="question-option-block__head">
                        <h3>Sub Question {index + 1}</h3>

                        {!readOnly && form.subQuestionEditorStates.length > 1 && (
                          <DeleteButton
                            label="Remove"
                            onClick={() => removeSubQuestion(index)}
                          />
                        )}
                      </div>

                      <div className="question-editor-section">
                        <h2>Question</h2>

                        <RichQuestionEditor
                          readOnly={readOnly}
                          editorState={editorState}
                          onEditorStateChange={(nextState) =>
                            updateSubQuestionEditor(index, nextState)
                          }
                        />
                      </div>

                      <div className="question-editor-section">
                        <h2>Model Answer</h2>

                        <RichQuestionEditor
                          readOnly={readOnly}
                          editorState={form.subQuestionAnswerEditorStates[index]}
                          onEditorStateChange={(nextState) =>
                            updateSubQuestionAnswerEditor(index, nextState)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {!readOnly && (
                  <MainButton
                    action="add"
                    icon="add"
                    label="Add Sub Question"
                    tone="primary"
                    onClick={addSubQuestion}
                  />
                )}
              </>
            )}

          </div>
        </div>
        
      </div>

      <PassageModal
        show={Boolean(passageModalMode)}
        mode={passageModalMode}
        passage={selectedPassageDetails}
        passageTypeId={form.selectedPassageType}
        onClose={() => setPassageModalMode(null)}
        onSubmit={async (payload) => {
          if (passageModalMode === "edit") {
            return editPassage(form.selectedPassage, payload);
          }

          return createPassage(payload);
        }}
      />
    </section>
  );
}
